import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps, ICommand } from '../../../core/tools/interfaces/ITool';
import { useDiagramStore } from '../../../stores/diagramStore';
import { usePerspectiveStore } from '../../../stores/perspectiveStore';
import { BatchUpdatePersonsCommand } from '../commands/BatchUpdatePersonsCommand';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Plus, Minus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { PaletteMultiSelect } from '../fields/components/PaletteMultiSelect';
import { GenogramPerspectiveProjection, SUPPRESSED_INHERITED_CULTURE_KEY } from '../services/GenogramPerspectiveProjection';
import { InheritedTagChips } from '../fields/components/InheritedTagChips';
import { buildSuppressionUpdates, collectSuppressedTags } from './utils/inheritedTagSuppression';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

const humanizeCultureTagKey = (tag: string): string =>
  tag
    .split('_')
    .map((part) => (part.length ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''))
    .join(' ');

type DiagramNodeLike = ReturnType<typeof useDiagramStore.getState>['nodes'][number];

const CultureTagPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const ids = Array.from(context.selection);
  const nodes = useDiagramStore(s => s.nodes);
  const persons = nodes.filter(n => ids.includes(n.id));

  if (persons.length === 0) {
    return null;
  }

  return <CultureTagPanelContent nodes={nodes} persons={persons} onExecute={onExecute} />;
};

const CultureTagPanelContent: React.FC<{
  nodes: DiagramNodeLike[];
  persons: DiagramNodeLike[];
  onExecute: IToolComponentProps['onExecute'];
}> = ({ nodes, persons, onExecute }) => {
  const { t } = useTranslation('genogram');
  const edges = useDiagramStore(s => s.edges);
  const culturePalette = usePerspectiveStore(s => s.culturePalette);
  const healthPalette = usePerspectiveStore(s => s.healthPalette);
  const setCultureColor = usePerspectiveStore(s => s.setCultureColor);
  const [isOpen, setIsOpen] = React.useState(() => {
    try {
      const saved = localStorage.getItem('personPanel.cultureOpen');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const toggleSection = () => {
    const next = !isOpen;
    setIsOpen(next);
    try {
      localStorage.setItem('personPanel.cultureOpen', JSON.stringify(next));
    } catch {}
  };

  const defaultColors = ['#84cc16','#3b82f6','#a855f7','#f59e0b','#ef4444','#10b981','#6366f1','#14b8a6','#f43f5e'];

  const ensureCultureColor = (tag: string) => {
    if (!culturePalette[tag]) {
      const idx = Math.abs(tag.split('').reduce((a,c)=>a+c.charCodeAt(0),0)) % defaultColors.length;
      setCultureColor(tag, defaultColors[idx]);
    }
  };

  const arraysEqual = React.useCallback((a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  }, []);

  const commitTags = React.useCallback((tags: string[]) => {
    if (persons.length === 0) return;
    const sanitized = Array.from(new Set(tags.map(t => t.trim()).filter(Boolean)));
    sanitized.forEach(ensureCultureColor);

    const batchSpecs = persons.map(p => {
      const existing = ((p as any).attrs?.culturalHeritageTags ?? (p as any).culturalHeritageTags) as string[] | undefined;
      const current = Array.isArray(existing) ? existing : [];
      if (arraysEqual(current, sanitized)) {
        return null;
      }
      return { nodeId: p.id, changes: { attrs: { culturalHeritageTags: sanitized } } };
    }).filter((spec): spec is { nodeId: string; changes: any } => Boolean(spec));

    if (batchSpecs.length === 0) {
      return;
    }

    onExecute([new BatchUpdatePersonsCommand(batchSpecs, `Update culture tags for ${batchSpecs.length} person(s)`) ]);
  }, [arraysEqual, ensureCultureColor, onExecute, persons]);

  const explicitTagSet = new Set<string>();
  persons.forEach(p => (Array.isArray(((p as any).attrs?.culturalHeritageTags ?? (p as any).culturalHeritageTags)) ? ((p as any).attrs?.culturalHeritageTags ?? (p as any).culturalHeritageTags) : []).forEach((t: string) => explicitTagSet.add(t)));
  const explicitTags = Array.from(explicitTagSet);

  const cultureMixById = React.useMemo(() => {
    const result = GenogramPerspectiveProjection.compute(nodes as any, edges as any, { culturePalette, healthPalette });
    const map = new Map<string, string[]>();
    result.culture.forEach((mix, nodeId) => {
      map.set(nodeId, mix.map(entry => entry.tag));
    });
    return map;
  }, [nodes, edges, culturePalette, healthPalette]);

  const overrideInheritanceEnabled = persons.every(p => ((p as any).attrs?.overrideCultureInheritance || (p as any).overrideCultureInheritance));
  const derivedInheritedTags = overrideInheritanceEnabled
    ? []
    : Array.from(new Set(
      persons.flatMap(p => (cultureMixById.get(p.id) || []).filter(tag => !explicitTagSet.has(tag)))
    ));
  const suppressedTags = overrideInheritanceEnabled
    ? []
    : collectSuppressedTags(persons, SUPPRESSED_INHERITED_CULTURE_KEY).filter(
      tag => !explicitTagSet.has(tag) && !derivedInheritedTags.includes(tag),
    );

  const setInheritedTagSuppressed = (tag: string, suppress: boolean) => {
    const batchSpecs = buildSuppressionUpdates(persons, SUPPRESSED_INHERITED_CULTURE_KEY, tag, suppress);
    if (batchSpecs.length === 0) return;
    onExecute([new BatchUpdatePersonsCommand(batchSpecs, `${suppress ? 'Remove' : 'Restore'} inherited culture tag "${tag}" for ${batchSpecs.length} person(s)`)]);
  };

  const cultureTagLabel = React.useCallback(
    (tag: string) => t(`settings.cultureTagLabels.${tag}` as any, { defaultValue: humanizeCultureTagKey(tag) }),
    [t],
  );

  const multiSelectOptions = React.useMemo(() => {
    const optionMap = new Map<string, { value: string; label: string; color?: string }>();

    Object.entries(culturePalette).forEach(([tag, color]) => {
      optionMap.set(tag, { value: tag, label: cultureTagLabel(tag), color });
    });

    [...explicitTags, ...derivedInheritedTags].forEach(tag => {
      if (!optionMap.has(tag)) {
        optionMap.set(tag, { value: tag, label: cultureTagLabel(tag) });
      }
    });
    return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [culturePalette, explicitTags, derivedInheritedTags, cultureTagLabel]);

  return (
    <>
      <div className="h-px bg-border my-4" />
      <div className="px-3 pr-6">
        <button
          type="button"
          onClick={toggleSection}
          className="flex items-center justify-between w-full mb-3 group"
        >
          <h3 className="text-[11px] font-semibold text-foreground uppercase tracking-wider">
            {t('culture.title', 'Culture')}
          </h3>
          <div className="opacity-70 group-hover:opacity-100 transition-opacity">
            {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          </div>
        </button>

        {isOpen && (
          <div className="space-y-3">
            <PaletteMultiSelect
              id="culture-tags"
              value={explicitTags}
              options={multiSelectOptions}
              placeholder={t('culture.selectCultureTags', 'Select culture tags')}
              allowCustomOptions
              onCommit={commitTags}
            />
            <InheritedTagChips
              testIdPrefix="tid.genogram.culture"
              inheritedTags={derivedInheritedTags}
              suppressedTags={suppressedTags}
              getLabel={cultureTagLabel}
              getColor={tag => culturePalette[tag]}
              onRemove={tag => setInheritedTagSuppressed(tag, true)}
              onRestore={tag => setInheritedTagSuppressed(tag, false)}
            />
            <div className="mt-2 flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">{t('culture.overrideInheritance', 'Override inheritance')}</Label>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => {
                const next = !overrideInheritanceEnabled;
                const batchSpecs = persons.map(p => ({ nodeId: p.id, changes: { attrs: { overrideCultureInheritance: next } } }));
                onExecute([new BatchUpdatePersonsCommand(batchSpecs, `Set overrideCultureInheritance=${next} for ${batchSpecs.length} person(s)`)]);
              }}>{overrideInheritanceEnabled ? t('culture.on', 'On') : t('culture.off', 'Off')}</Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export class CultureTagTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-culture-tags',
    name: i18n.t('genogram:tools.cultureTags', { defaultValue: 'Culture Tags' }) as string,
    icon: 'tag',
    category: 'edit',
    description: 'Manage culture tags and colors',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram'],
    requiresSelection: true,
    minSelection: 1,
    ui: { toolbar: false, contextMenu: false, propertyPanel: true, sidebar: false, floatingPanel: false, statusBar: false },
    capabilities: { modifiesShapes: true, createsShapes: false, deletesShapes: false, modifiesConnectors: false, createsConnectors: false, deletesConnectors: false, requiresUserInput: true, supportsMultiSelect: true, supportsBatchOperations: true }
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.cultureTags', 'Culture Tags');
  }

  canExecute(context: IToolContext): boolean {
    if (context.diagramType !== 'genogram') return false;
    const ids = Array.from(context.selection);
    if (ids.length === 0) return false;
    const nodes = useDiagramStore.getState().nodes.filter(n => ids.includes(n.id));
    if (nodes.length === 0) return false;
    return nodes.every(n => n.type === 'person');
  }
  execute(params?: unknown): ICommand | ICommand[] | null { return Array.isArray(params) ? params as ICommand[] : null; }
  getPropertyPanelComponent() { return CultureTagPanel; }
}
